import React from 'react';
import { Menu, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { safeGetStorageItem, safeSetStorageItem } from '../utils/browserStorage';
import { cn } from './index';
import './sidebar.css';

const SIDEBAR_STORAGE_KEY = 'botspot_authenticated_sidebar_collapsed';
const isMobileViewport = () => window.matchMedia?.('(max-width: 899px)')?.matches ?? false;

type SidebarContextValue = {
  closeMobile: () => void;
  collapsed: boolean;
  isMobile: boolean;
  mobileOpen: boolean;
  toggle: () => void;
};

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

export function useSidebar(): SidebarContextValue {
  const context = React.useContext(SidebarContext);
  if (!context) throw new Error('useSidebar must be used within SidebarProvider');
  return context;
}

export function SidebarProvider({ children }: React.PropsWithChildren): React.ReactElement {
  const [collapsed, setCollapsed] = React.useState(() => {
    return safeGetStorageItem('localStorage', SIDEBAR_STORAGE_KEY) === 'true';
  });
  const [isMobile, setIsMobile] = React.useState(isMobileViewport);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const toggle = React.useCallback(() => {
    if (isMobileViewport()) {
      setMobileOpen((open) => !open);
      return;
    }
    setCollapsed((current) => {
      const next = !current;
      safeSetStorageItem('localStorage', SIDEBAR_STORAGE_KEY, String(next));
      return next;
    });
  }, []);
  const closeMobile = React.useCallback(() => setMobileOpen(false), []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'b') {
        event.preventDefault();
        toggle();
      }
      if (event.key === 'Escape') closeMobile();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeMobile, toggle]);

  React.useEffect(() => {
    const media = window.matchMedia?.('(max-width: 899px)');
    if (!media) return undefined;
    const handleChange = () => {
      setIsMobile(media.matches);
      if (!media.matches) setMobileOpen(false);
    };
    handleChange();
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [mobileOpen]);

  const value = React.useMemo(
    () => ({ closeMobile, collapsed, isMobile, mobileOpen, toggle }),
    [closeMobile, collapsed, isMobile, mobileOpen, toggle],
  );
  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

type SidebarProps = React.HTMLAttributes<HTMLElement> & { label?: string };

export function Sidebar({
  children,
  className,
  label = 'Product navigation',
  ...props
}: SidebarProps): React.ReactElement {
  const { closeMobile, collapsed, isMobile, mobileOpen } = useSidebar();
  const visuallyCollapsed = !isMobile && collapsed;
  return (
    <>
      <button
        aria-label="Close product navigation"
        className={cn('bsui-sidebar-backdrop', mobileOpen && 'is-open')}
        onClick={closeMobile}
        tabIndex={mobileOpen ? 0 : -1}
        type="button"
      />
      <aside
        aria-hidden={isMobile && !mobileOpen ? true : undefined}
        aria-label={label}
        className={cn('bsui-sidebar', className, mobileOpen && 'is-mobile-open')}
        data-collapsed={visuallyCollapsed ? 'true' : 'false'}
        inert={isMobile && !mobileOpen ? true : undefined}
        {...props}
      >
        {children}
      </aside>
    </>
  );
}

export function SidebarHeader(props: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return <div {...props} className={cn('bsui-sidebar-header', props.className)} />;
}
export function SidebarContent(props: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return <div {...props} className={cn('bsui-sidebar-content', props.className)} />;
}
export function SidebarFooter(props: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return <div {...props} className={cn('bsui-sidebar-footer', props.className)} />;
}
export function SidebarGroup(props: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return <div {...props} className={cn('bsui-sidebar-group', props.className)} />;
}
export function SidebarGroupLabel(props: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return <div {...props} className={cn('bsui-sidebar-group-label', props.className)} />;
}
export function SidebarMenu(props: React.HTMLAttributes<HTMLUListElement>): React.ReactElement {
  return <ul {...props} className={cn('bsui-sidebar-menu', props.className)} />;
}
export function SidebarMenuItem(props: React.HTMLAttributes<HTMLLIElement>): React.ReactElement {
  return <li {...props} className={cn('bsui-sidebar-menu-item', props.className)} />;
}

export const SidebarInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(function SidebarInput({ className, ...props }, ref): React.ReactElement {
  return <input ref={ref} className={cn('bsui-sidebar-input', className)} {...props} />;
});

export function SidebarTrigger({ mobile = false }: { mobile?: boolean }): React.ReactElement {
  const { collapsed, isMobile, mobileOpen, toggle } = useSidebar();
  const label = mobileOpen
    ? 'Close product navigation'
    : collapsed
      ? 'Expand product navigation'
      : 'Collapse product navigation';
  const Icon =
    mobile && !mobileOpen ? Menu : mobileOpen ? X : collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <button
      aria-controls="authenticated-product-sidebar"
      aria-expanded={mobile || isMobile ? mobileOpen : !collapsed}
      aria-label={
        mobile ? (mobileOpen ? 'Close product navigation' : 'Open product navigation') : label
      }
      className={cn('bsui-sidebar-trigger', mobile && 'bsui-sidebar-trigger--mobile')}
      onClick={toggle}
      title={mobile ? undefined : `${label} (⌘B)`}
      type="button"
    >
      <Icon aria-hidden="true" size={17} strokeWidth={1.8} />
    </button>
  );
}
